import {
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  Flame,
  Layers3,
  RotateCcw,
  Sparkles,
  Target
} from "lucide-react";
import { birds, groupMeta, type BirdGroup } from "../data/birds";
import type { ProgressState } from "../hooks/useProgress";

type HomeViewProps = {
  progress: ProgressState;
  stats: {
    known: number;
    fuzzy: number;
    learning: number;
    today: number;
    streak: number;
  };
  onStartLearning: () => void;
  onStartReview: () => void;
  onStartQuiz: () => void;
  onOpenGroup: (group: BirdGroup) => void;
};

const dateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const HomeView = ({
  progress,
  stats,
  onStartLearning,
  onStartReview,
  onStartQuiz,
  onOpenGroup
}: HomeViewProps) => {
  const dailyPercent = Math.min(
    100,
    Math.round((stats.today / progress.dailyGoal) * 100)
  );
  const reviewCount = new Set([
    ...progress.mistakes,
    ...birds
      .filter((bird) =>
        ["fuzzy", "new"].includes(progress.mastery[bird.id] ?? "")
      )
      .map((bird) => bird.id)
  ]).size;
  const week = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - index));
    return {
      key: dateKey(date),
      day: ["日", "一", "二", "三", "四", "五", "六"][date.getDay()],
      date: date.getDate(),
      studied: Boolean(progress.studyLog[dateKey(date)]),
      today: index === 6
    };
  });

  const groupEntries = Object.entries(groupMeta) as [
    BirdGroup,
    (typeof groupMeta)[BirdGroup]
  ][];

  return (
    <main className="page home-page">
      <section className="home-hero">
        <div className="home-hero__content">
          <div className="eyebrow">
            <Sparkles size={15} />
            中国常见鸟类 · 61 图速通
          </div>
          <h1>
            认鸟不用死记，
            <br />
            先抓住<em>一眼特征</em>
          </h1>
          <p>
            每次认识几只鸟，再用相似种对比巩固。下一次在路边遇见，你会知道该看哪里。
          </p>
          <div className="home-hero__actions">
            <button className="button button--primary" onClick={onStartLearning}>
              <BookOpen size={19} />
              开始今日学习
              <ChevronRight size={18} />
            </button>
            <button className="button button--ghost" onClick={onStartQuiz}>
              先测一测
            </button>
          </div>
          <div className="hero-proof">
            <span>
              <strong>61</strong>
              张学习卡
            </span>
            <span>
              <strong>63</strong>
              幅实拍照片
            </span>
            <span>
              <strong>1v1</strong>
              相似种对比
            </span>
          </div>
        </div>
        <div className="home-hero__visual" aria-label="普通翠鸟学习卡预览">
          <img src="/birds/007.webp" alt="普通翠鸟停在水边" />
          <div className="hero-card hero-card--name">
            <span>今日第一只</span>
            <strong>普通翠鸟</strong>
          </div>
          <div className="hero-card hero-card--tip">
            <Target size={17} />
            <span>
              <small>一眼特征</small>
              背蓝 · 腹橙 · 短尾
            </span>
          </div>
          <span className="hero-stamp">从照片开始</span>
        </div>
      </section>

      <section className="dashboard-grid">
        <article className="panel today-panel">
          <div className="panel__heading">
            <div>
              <span className="section-kicker">今日计划</span>
              <h2>再认识 {Math.max(0, progress.dailyGoal - stats.today)} 只鸟</h2>
            </div>
            <div
              className="progress-ring"
              style={{ "--progress": `${dailyPercent * 3.6}deg` } as React.CSSProperties}
              aria-label={`今日目标完成 ${dailyPercent}%`}
            >
              <strong>{dailyPercent}%</strong>
            </div>
          </div>
          <div className="week-strip">
            {week.map((item) => (
              <div
                className={`${item.studied ? "is-done" : ""} ${
                  item.today ? "is-today" : ""
                }`}
                key={item.key}
              >
                <small>周{item.day}</small>
                <span>{item.studied ? "✓" : item.date}</span>
              </div>
            ))}
          </div>
          <div className="today-panel__footer">
            <div className="streak-note">
              <Flame size={20} />
              <span>
                <strong>{stats.streak || "从今天开始"}</strong>
                {stats.streak ? " 天连续学习" : "，建立你的认鸟节奏"}
              </span>
            </div>
            <button className="text-button" onClick={onStartLearning}>
              继续学习 <ArrowUpRight size={16} />
            </button>
          </div>
        </article>

        <article className="panel review-panel">
          <div className="review-panel__icon">
            <RotateCcw size={26} />
          </div>
          <span className="section-kicker">记忆加固</span>
          <h2>{reviewCount} 只鸟需要再看</h2>
          <p>
            系统会优先安排答错和“有点模糊”的鸟，避免只复习已经认识的。
          </p>
          <button
            className="button button--ink"
            onClick={onStartReview}
            disabled={!reviewCount}
          >
            开始复习
            <ChevronRight size={17} />
          </button>
        </article>
      </section>

      <section className="section course-section">
        <div className="section-heading">
          <div>
            <span className="section-kicker">分类学习</span>
            <h2>按观察场景建立识别框架</h2>
          </div>
          <span className="section-heading__note">
            <Layers3 size={17} /> 6 个主题
          </span>
        </div>
        <div className="course-grid">
          {groupEntries.map(([group, meta], index) => {
            const groupBirds = birds.filter((bird) => bird.group === group);
            const known = groupBirds.filter(
              (bird) => progress.mastery[bird.id] === "known"
            ).length;
            const percent = Math.round((known / groupBirds.length) * 100);
            return (
              <button
                className={`course-card tone-${meta.tone}`}
                key={group}
                onClick={() => onOpenGroup(group)}
              >
                <span className="course-card__index">
                  {`${index + 1}`.padStart(2, "0")}
                </span>
                <div>
                  <small>{meta.short}主题</small>
                  <h3>{group}</h3>
                  <p>{meta.description}</p>
                </div>
                <div className="course-card__progress">
                  <span>
                    {known}/{groupBirds.length}
                  </span>
                  <i>
                    <b style={{ width: `${percent}%` }} />
                  </i>
                </div>
                <ChevronRight className="course-card__arrow" size={20} />
              </button>
            );
          })}
        </div>
      </section>
    </main>
  );
};
